import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { FormattedMessage, useIntl } from 'react-intl';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { resolvePath, RouteConfig, useNavigate, useQuery } from '../../../routes';
import routes, { order, payment, plan, sso } from '../../routes';
import { fromAccounts, fromLayout, fromOrder, fromOrganizations, fromPaymentMethod, fromSubscription, useAppDispatch } from '../../../store';
import { useLaunchDarkly } from '../../../launchdarkly';
import { PaymentMethodType, SubscriptionSource } from '../../../models';
import { ExternalIcon, ShieldIcon } from './SidebarIcons';

const NAV_GROUPS: { key: 'manage' | 'billing'; labelId: string }[] = [
  { key: 'manage', labelId: 'layout.sidebar.group.manage' },
  { key: 'billing', labelId: 'layout.sidebar.group.billing' },
];

const navItemSx = (open: boolean, isSelected: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: open ? 'flex-start' : 'center',
  gap: '13px',
  width: '100%',
  padding: '11px 12px',
  borderRadius: '10px',
  fontSize: 14.5,
  fontWeight: isSelected ? 600 : 500,
  color: isSelected ? '#063527' : '#1f2430',
  backgroundColor: isSelected ? '#cfe6df' : 'transparent',
  boxShadow: isSelected ? 'inset 3px 0 0 #0f8461' : 'none',
  textAlign: 'left',
  textDecoration: 'none',
  transition: 'background-color .15s ease, color .15s ease',
  '& .nav-ic': {
    display: 'inline-flex',
    color: isSelected ? '#0f8461' : '#5f5d75',
    transition: 'color .15s ease',
  },
  '& .nav-ext': {
    display: 'inline-flex',
    color: '#8a9490',
    transition: 'color .15s ease',
  },
  '&:hover': {
    backgroundColor: isSelected ? '#bfddd4' : '#f0faf5',
  },
  '&:hover .nav-ic, &:hover .nav-ext': {
    color: '#0f8461',
  },
  '&:focus-visible': {
    outline: '2px solid #0f8461',
    outlineOffset: '2px',
  },
} as const);

const groupLabelSx = {
  padding: '14px 12px 5px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '.09em',
  textTransform: 'uppercase',
  color: '#8a9490',
} as const;

interface TabMenuProps {
  open: boolean;
  filteredRoutes: RouteConfig[];
}

const TabMenu = ({ open, filteredRoutes }: TabMenuProps) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const { get } = useQuery();
  const { pathname } = useLocation();
  const { navigate, replaceQuery } = useNavigate();
  const { flags } = useLaunchDarkly();
  const accountId = Number(get('accountId'));
  const ftic = get('ftic') === 'true';
  const currentRoute = routes.find(route => pathname === route.path);
  const currentDefaultPaymentMethod = useSelector(fromPaymentMethod.selectDefaultPaymentMethod);
  const hadActivePaidSubscription = useSelector(
    fromSubscription.selectSubscriptionsBySource(SubscriptionSource.RECURLY),
  ).some(sub => new Date(sub.expiryDate).getTime() > Date.now());
  const previousOrders = useSelector(fromOrder.selectPreviousOrder);
  const pendingOrders = useSelector(fromOrder.selectPendingOrder);
  const hasAnyOrder = previousOrders.length > 0 || pendingOrders.length > 0;
  // Only fetch once an account is selected — doSetSelectedAccount also sets the
  // X-Account-Id header that /v2/ce/orders requires, so fetching earlier returns
  // 400 account.not.provided.
  const selectedAccountId = useSelector(fromAccounts.selectSelectedAccountId);
  const selectedAccount = useSelector(fromAccounts.selectSelectedAccount);
  const orgId = useSelector(fromOrganizations.selectFirstOrganization(Number(accountId)))?.id;

  useEffect(() => {
    if (selectedAccountId) {
      dispatch(fromOrder.doFetchAllOrder(selectedAccountId));
    }
  }, [dispatch, selectedAccountId]);

  useEffect(() => {
    if (accountId) {
      dispatch(fromOrganizations.doFetchOrganizationByAccountId({ accountId }));
    }
  }, [dispatch, accountId]);

  const finalRoutes = filteredRoutes.filter(route => {
    if (!route.showInMenu) {
      return false;
    }
    if (route.path === order.path) {
      return hasAnyOrder && route.version === currentRoute?.version;
    }
    // Hide plan tab for AWS Marketplace users since they should use the dialog instead
    if (currentDefaultPaymentMethod === PaymentMethodType.AWS_MARKETPLACE
      && route.path === plan.path) {
      return false;
    }

    // New account (no active paid subscription) - only show plan and payment tabs
    if (!hadActivePaidSubscription && flags?.truePlatformEnabled) {
      return route.path.startsWith(plan.path) || route.path === payment.path;
    }

    return route.version === currentRoute?.version;
  });

  // Mirror ButtonToLicenseManagement: G3 accounts → True Platform admin page;
  // legacy accounts → KO admin license page via SSO (needs the account's first org).
  const resolveLicenseManagementUrl = () => {
    if (!selectedAccount) return undefined;
    if (selectedAccount.g3) {
      const finalDomain = selectedAccount.domain || `ac-${selectedAccount.id}`;
      return `https://${finalDomain}.${process.env.REACT_APP_CUSTOM_DOMAIN_POSTFIX}/admin/account/license-management`;
    }
    if (!orgId) return undefined;
    return resolvePath(sso, undefined, {
      redirect_uri: `${process.env.REACT_APP_ADMIN_URL}/organization/${orgId}/admin/license_keys/kse`,
    });
  };
  const licenseManagementUrl = resolveLicenseManagementUrl();

  const renderNavItem = (route: RouteConfig) => {
    const isSelected = pathname.startsWith(route.path);
    return (
      <ButtonBase
        key={route.path}
        title={intl.formatMessage({ id: route.pageTitle })}
        aria-current={isSelected ? 'page' : undefined}
        onClick={() => {
          if (currentRoute?.path === route.path) {
            return;
          }
          dispatch(fromLayout.setLoading(true));
          navigate(
            route.path,
            replaceQuery({ accountId, ftic }),
          );
        }}
        sx={navItemSx(open, isSelected)}
      >
        <span className="nav-ic">{route.icon}</span>
        {open && (
          <Box component="span" sx={{ flex: 1 }}>
            <FormattedMessage id={route.pageTitle} />
          </Box>
        )}
      </ButtonBase>
    );
  };

  return (
    <Box
      component="nav"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        padding: open ? '0 14px' : '0 10px',
      }}
    >
      {NAV_GROUPS.map((group, groupIndex) => {
        const groupRoutes = finalRoutes.filter(
          route => (route.menuGroup ?? 'manage') === group.key,
        );
        if (groupRoutes.length === 0) {
          return null;
        }
        return (
          <Box key={group.key} sx={{ display: 'contents' }}>
            {open && (
              <Typography sx={{ ...groupLabelSx, ...(groupIndex === 0 ? { paddingTop: '4px' } : {}) }}>
                <FormattedMessage id={group.labelId} />
              </Typography>
            )}
            {groupRoutes.map(renderNavItem)}
          </Box>
        );
      })}
      {licenseManagementUrl && (
        <>
          <Box sx={{ height: '1px', backgroundColor: '#e8edea', margin: '12px 4px 8px' }} />
          <ButtonBase
            component="a"
            href={licenseManagementUrl}
            target="_blank"
            rel="noopener noreferrer"
            title={intl.formatMessage({ id: 'layout.sidebar.license.management' })}
            sx={navItemSx(open, false)}
          >
            <span className="nav-ic"><ShieldIcon /></span>
            {open && (
              <>
                <Box component="span" sx={{ flex: 1 }}>
                  <FormattedMessage id="layout.sidebar.license.management" />
                </Box>
                <span className="nav-ext"><ExternalIcon /></span>
              </>
            )}
          </ButtonBase>
        </>
      )}
    </Box>
  );
};

export default TabMenu;
